import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { useAuth } from '@shared/auth/AuthContext';
import { Skeleton, SectionErrorBoundary, Button } from '@shared/ui';
import { NamedIcon } from '@shared/ui/icons';
import { formatDate } from '@shared/lib/format';
import { captureError } from '@shared/lib/observability';
import { UnreadBadge } from '@shared/ui/UnreadBadge';
import { useUnreadThreads } from '@shared/hooks/useUnreadThreads';
import CampaignForm from './CampaignForm';
import CampaignApplicants from './CampaignApplicants';

// Business "My campaigns": the jobs this customer posted for creators to apply to.
// Replaces the AI brief / analyst-broker Requests tab. Applicant-review screen is next.

interface Row {
  id: string;
  title: string;
  status: string;
  compensation_type: string;
  budget: number | null;
  budget_basis: string;
  currency: string;
  max_creators: number;
  apply_deadline: string | null;
  created_at: string;
  categories: CatName | CatName[] | null;
  campaign_applications: { count: number }[] | null;
}
type CatName = { name_ru: string | null; name_en: string | null; name_ky: string | null };
const catName = (c: CatName | null, lng: string): string =>
  (lng === 'en' ? c?.name_en : lng === 'ky' ? c?.name_ky : c?.name_ru) || c?.name_ru || c?.name_en || '—';

const STATUS: Record<string, { label: string; color: string }> = {
  draft: { label: 'Черновик', color: '#bcd0ff' },
  open: { label: 'Активно', color: '#5fd17a' },
  filled: { label: 'Набрано', color: '#f5b700' },
  closed: { label: 'Закрыто', color: '#7a8' },
  cancelled: { label: 'Отменено', color: '#888' },
  pending_review: { label: 'На проверке', color: '#f5b700' },
};
const rel = <T,>(v: T | T[] | null | undefined): T | null => (!v ? null : Array.isArray(v) ? v[0] ?? null : v);
const fmtMoney = (n: number | null, cur: string) => (n == null ? null : `${n.toLocaleString('ru-RU')} ${cur === 'KGS' ? 'сом' : cur}`);

export default function Campaigns() {
  const { t, i18n } = useTranslation('customer');
  const tr = (k: string, d: string, o?: Record<string, unknown>) => t(k, { defaultValue: d, ...o });
  const { user } = useAuth();
  const invalidate = useInvalidateSupabase();
  const [showForm, setShowForm] = useState(false);
  const [openApplicants, setOpenApplicants] = useState<Row | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [filter, setFilter] = useState<'active' | 'archive'>('active');
  const { byCampaign } = useUnreadThreads();

  const { data, isLoading, error, refetch } = useSupabaseQuery<Row[]>(
    ['my-campaigns', user?.id],
    async () => {
      if (!user) return { data: [], error: null };
      return supabase
        .from('creator_campaigns')
        .select('id, title, status, compensation_type, budget, budget_basis, currency, max_creators, apply_deadline, created_at, categories ( name_ru, name_en, name_ky ), campaign_applications ( count )')
        .eq('customer_id', user.id)
        .order('created_at', { ascending: false })
        .limit(100);
    },
    { enabled: !!user },
  );

  const act = async (id: string, rpc: 'publish_campaign' | 'close_campaign') => {
    setBusy(id);
    const { error: e } = await supabase.rpc(rpc, { p_id: id });
    setBusy(null);
    if (e) { captureError(e, { where: `campaign.${rpc}`, id }); return; }
    if (rpc === 'publish_campaign') {
      // fire-and-forget Web Push to matching creators
      supabase.functions.invoke('send-campaign-push', { body: { campaign_id: id } }).catch(() => {});
    }
    invalidate(['my-campaigns']);
    refetch();
  };

  const rows = data ?? [];
  const isArchived = (s: string) => s === 'closed' || s === 'cancelled';
  const activeCount = rows.filter((r) => !isArchived(r.status)).length;
  const archiveCount = rows.length - activeCount;
  const visible = rows.filter((r) => (filter === 'archive' ? isArchived(r.status) : !isArchived(r.status)));

  return (
    <SectionErrorBoundary name="campaigns">
     <div style={{ maxWidth: 960, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h2 className="c-page-title" style={{ margin: 0 }}>{tr('campaign.pageTitle', 'Мои задания')}</h2>
        <Button variant="primary" onClick={() => setShowForm(true)}>+ {tr('campaign.new', 'Разместить задание')}</Button>
      </div>
      <p style={{ color: 'var(--muted)', fontSize: 13, margin: '0 0 16px' }}>
        {tr('campaign.pageSub', 'Опубликуйте задание — оно появится у блогеров в разделе «Спрос», и они откликнутся.')}
      </p>

      {rows.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {([['active', tr('campaign.tabActive', 'Активные'), activeCount], ['archive', tr('campaign.tabArchive', 'Архив'), archiveCount]] as const).map(([f, label, count]) => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ padding: '6px 14px', borderRadius: 16, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
                border: `1px solid ${filter === f ? 'var(--accent)' : 'rgba(15,45,100,0.5)'}`,
                background: filter === f ? 'rgba(74,158,255,0.14)' : 'transparent',
                color: filter === f ? 'var(--accent-text)' : 'var(--muted)' }}>
              {label} <span style={{ opacity: 0.7 }}>{count}</span>
            </button>
          ))}
        </div>
      )}

      {isLoading && <><Skeleton height={72} style={{ marginBottom: 10 }} /><Skeleton height={72} /></>}
      {error && <div style={{ color: 'var(--danger, #e74c3c)' }}>{String((error as Error)?.message ?? error)}</div>}

      {!isLoading && !error && rows.length === 0 && (
        <div className="empty-state">
          <div style={{ marginBottom: 10, opacity: 0.45 }}><NamedIcon name="megaphone" size={34} color="#6b7688" /></div>
          <p style={{ margin: 0 }}>{tr('campaign.empty', 'Пока нет заданий. Нажмите «Разместить задание», чтобы найти блогеров.')}</p>
        </div>
      )}
      {!isLoading && !error && rows.length > 0 && visible.length === 0 && (
        <div style={{ color: 'var(--dim)', fontSize: 13, padding: '16px 2px' }}>
          {filter === 'archive' ? tr('campaign.emptyArchive', 'Нет закрытых заданий.') : tr('campaign.emptyActive', 'Нет активных заданий.')}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {visible.map((r) => {
          const st = STATUS[r.status] ?? { label: r.status, color: '#7a8' };
          const applicants = r.campaign_applications?.[0]?.count ?? 0;
          const money = r.compensation_type === 'barter' ? tr('campaign.barter', 'Бартер') : fmtMoney(r.budget, r.currency);
          return (
            <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, flexWrap: 'wrap', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(15,45,100,0.4)', borderRadius: 12, padding: '16px 20px' }}>
              {/* left: title block */}
              <div style={{ minWidth: 220, flex: 1 }}>
                <div style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 5 }}>{catName(rel(r.categories), i18n.language)}</div>
                <div style={{ fontSize: 16, color: 'var(--text)', fontWeight: 600, marginBottom: 9 }}>{r.title}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, color: 'var(--muted)', fontSize: 12.5 }}>
                  {money && (
                    <span style={{ color: 'var(--text-2)' }}>
                      {money}
                      {r.compensation_type !== 'barter' && r.budget != null &&
                        ` / ${r.budget_basis === 'total' ? tr('campaign.perTotal', 'всего') : tr('campaign.perCreator', 'за блогера')}`}
                    </span>
                  )}
                  {r.apply_deadline && <span>{tr('campaign.applyByShort', 'заявки до')} {formatDate(r.apply_deadline, i18n.language)}</span>}
                  <span>{t('campaign.need', { count: r.max_creators })}</span>
                </div>
              </div>
              {/* right rail: date + status + actions */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 12, flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11.5, color: 'var(--dim)' }}>{formatDate(r.created_at, i18n.language)}</span>
                  <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, color: st.color, background: `${st.color}22`, border: `1px solid ${st.color}55` }}>{tr(`campaign.status.${r.status}`, st.label)}</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {(r.status === 'draft' || r.status === 'pending_review') && (
                    <Button variant="primary" onClick={() => act(r.id, 'publish_campaign')} loading={busy === r.id} style={{ minHeight: 34, padding: '5px 16px' }}>{tr('campaign.publish', 'Опубликовать')}</Button>
                  )}
                  {r.status !== 'draft' && r.status !== 'pending_review' && (
                    <Button variant="primary" onClick={() => setOpenApplicants(r)} style={{ minHeight: 34, padding: '5px 16px', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                      {tr('campaign.viewApplicants', 'Отклики ({{n}})', { n: applicants })}
                      <UnreadBadge count={byCampaign.get(r.id) ?? 0} />
                    </Button>
                  )}
                  {(r.status === 'open' || r.status === 'filled') && (
                    <Button variant="secondary" onClick={() => act(r.id, 'close_campaign')} loading={busy === r.id} style={{ minHeight: 34, padding: '5px 16px', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-2)' }}>{tr('campaign.close', 'Закрыть')}</Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showForm && <CampaignForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); refetch(); }} />}
      {openApplicants && (
        <CampaignApplicants
          campaignId={openApplicants.id}
          title={openApplicants.title}
          maxCreators={openApplicants.max_creators}
          budget={openApplicants.budget}
          budgetBasis={openApplicants.budget_basis}
          compensationType={openApplicants.compensation_type}
          onClose={() => setOpenApplicants(null)}
          onChanged={refetch}
        />
      )}
     </div>
    </SectionErrorBoundary>
  );
}
